import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";

const initialState = {
  notes: {}, // { noteFor: { entityId: [notes] } }
  loading: false,
  error: null,
};

// Action Types
const FETCH_NOTES_REQUEST = "FETCH_NOTES_REQUEST";
const FETCH_NOTES_SUCCESS = "FETCH_NOTES_SUCCESS";
const FETCH_NOTES_FAILURE = "FETCH_NOTES_FAILURE";
const CREATE_NOTE_SUCCESS = "CREATE_NOTE_SUCCESS";
const UPDATE_NOTE_SUCCESS = "UPDATE_NOTE_SUCCESS";
const DELETE_NOTE_SUCCESS = "DELETE_NOTE_SUCCESS";
const NOTE_OPERATION_FAILURE = "NOTE_OPERATION_FAILURE";

// Thunks
export const fetchNotes =
  (noteFor, entityId, sortField = "id", sortDirection = "asc") =>
  async (dispatch) => {
    const service = getService(serviceNames.perNotes);
    const query = {
      noteFor,
      entityId,
      isDeleted: false,
      $limit: -1,
      $sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
    };

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: FETCH_NOTES_REQUEST,
        success: FETCH_NOTES_SUCCESS,
        failure: FETCH_NOTES_FAILURE,
      },
      apiCall: () => service.find({ query }),
      transformResult: (result) => ({ noteFor, entityId, notes: result }),
      errorMessagePrefix: "Failed to fetch notes",
    });
  };

export const createNote = (noteData) => async (dispatch) => {
  const service = getService(serviceNames.perNotes);

  return await asyncThunkWrapper({
    dispatch,
    types: {
      request: FETCH_NOTES_REQUEST,
      success: CREATE_NOTE_SUCCESS,
      failure: NOTE_OPERATION_FAILURE,
    },
    apiCall: () => service.create(noteData),
    transformResult: (result) => ({
      ...result,
      noteFor: noteData.noteFor,
      entityId: noteData.entityId,
    }),
    successMessage: "Note created successfully",
    errorMessagePrefix: "Failed to create note",
  });
};

export const updateNote = (id, noteData) => async (dispatch) => {
  const service = getService(serviceNames.perNotes);

  return await asyncThunkWrapper({
    dispatch,
    types: {
      request: FETCH_NOTES_REQUEST,
      success: UPDATE_NOTE_SUCCESS,
      failure: NOTE_OPERATION_FAILURE,
    },
    apiCall: () => service.patch(id, noteData),
    transformResult: (result) => ({
      ...result,
      noteFor: noteData.noteFor,
      entityId: noteData.entityId,
      id,
    }),
    successMessage: "Note updated successfully",
    errorMessagePrefix: "Failed to update note",
  });
};

export const deleteNote =
  (id, userId, noteFor, entityId) => async (dispatch) => {
    const service = getService(serviceNames.perNotes);
    const patchData = {
      isDeleted: true,
      deletedBy: userId,
      deletedAt: new Date().toISOString(),
    };

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: FETCH_NOTES_REQUEST,
        success: DELETE_NOTE_SUCCESS,
        failure: NOTE_OPERATION_FAILURE,
      },
      apiCall: () => service.patch(id, patchData),
      transformResult: () => ({ id, noteFor, entityId }),
      successMessage: "Note deleted successfully",
      errorMessagePrefix: "Failed to delete note",
    });
  };

// Reducer
export default function perNotesReducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_NOTES_REQUEST:
      return { ...state, loading: true, error: null };

    case FETCH_NOTES_SUCCESS:
      return {
        ...state,
        loading: false,
        notes: {
          ...state.notes,
          [action.payload.noteFor]: {
            ...state.notes[action.payload.noteFor],
            [action.payload.entityId]: action.payload.notes,
          },
        },
      };

    case CREATE_NOTE_SUCCESS: {
      const { noteFor, entityId } = action.payload;
      const existingNotes = state.notes[noteFor]?.[entityId] ?? [];

      return {
        ...state,
        loading: false,
        notes: {
          ...state.notes,
          [noteFor]: {
            ...state.notes[noteFor],
            [entityId]: [...existingNotes, action.payload],
          },
        },
      };
    }

    case UPDATE_NOTE_SUCCESS:
      return {
        ...state,
        loading: false,
        notes: {
          ...state.notes,
          [action.payload.noteFor]: {
            ...state.notes[action.payload.noteFor],
            [action.payload.entityId]: state.notes[action.payload.noteFor][
              action.payload.entityId
            ].map((note) =>
              note.id === action.payload.id ? action.payload : note
            ),
          },
        },
      };

    case DELETE_NOTE_SUCCESS:
      return {
        ...state,
        loading: false,
        notes: {
          ...state.notes,
          [action.payload.noteFor]: {
            ...state.notes[action.payload.noteFor],
            [action.payload.entityId]: state.notes[action.payload.noteFor][
              action.payload.entityId
            ].filter((note) => note.id !== action.payload.id),
          },
        },
      };

    case FETCH_NOTES_FAILURE:
    case NOTE_OPERATION_FAILURE:
      return { ...state, loading: false, error: action.payload };

    default:
      return state;
  }
}
